import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useAppSelector } from "app/hooks";
import useVisibleMenuItems from "features/app-layout/layout/layout-side-menu/use-visible-menu-items.hook";
import { useSchedulerChoice } from "features/app-layout/scheduler-choice/use-scheduler-choice.hook";
import {
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentUser,
  selectHasRegions
} from "features/auth/auth-slice";
import useReportPermissions from "features/reports/hooks/use-report-permissions.hook";
import useUserRoles from "hooks/use-user-roles.hook";
import { useGetPracticeBillingSettingsQuery } from "services/practices";

import type { QuickLauncherAction } from "../quick-launcher.types";
import type { DestinationAccess } from "./destination.types";
import { QUICK_LAUNCHER_DESTINATIONS } from "./index";
import isDestinationVisible from "./is-destination-visible.util";

const NO_DESTINATIONS: QuickLauncherAction[] = [];

/**
 * The "Go to" section: every sub-page, report, settings page and tab this user
 * could reach from a section's own menu, as search-only launcher actions.
 *
 * `enabled` is "the launcher is open". The billing-settings read is the one
 * fetch any gate needs, and it waits for it, so a user who never opens the
 * launcher never pays for it. Until it lands the two payment reports are
 * simply absent; nothing else waits on it.
 */
export default function useQuickLauncherDestinations(
  enabled: boolean
): QuickLauncherAction[] {
  const navigate = useNavigate();
  const flags = useFlags();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const hasRegions = Boolean(useAppSelector(selectHasRegions));
  const roles = useUserRoles();
  const reportAccess = useReportPermissions();
  const visibleMenuItems = useVisibleMenuItems();
  const { useV3 } = useSchedulerChoice();
  const { data: practiceBillingSettings } = useGetPracticeBillingSettingsQuery({
    skip: !enabled
  });

  return useMemo(() => {
    if (!enabled) return NO_DESTINATIONS;

    // The side menu rewrites Scheduling to /scheduling-v3 for someone who chose
    // it; keying sections by the original path keeps one table for both.
    const sections = new Map(
      visibleMenuItems.map((item) => [
        useV3 && item.to === "/scheduling-v3" ? "/scheduling" : item.to,
        item
      ])
    );
    const access: DestinationAccess = {
      flags,
      currentUser,
      currentPractice,
      currentClinic,
      hasRegions,
      roles,
      reportAccess,
      practiceBillingSettings,
      sectionPaths: new Map(
        [...sections].map(([section, item]) => [section, item.to])
      )
    };

    return QUICK_LAUNCHER_DESTINATIONS.filter((destination) =>
      isDestinationVisible(destination, access)
    ).flatMap((destination): QuickLauncherAction[] => {
      const sectionItem = sections.get(destination.section);
      if (!sectionItem) return [];
      const path = `${sectionItem.to}/${destination.to}`;
      const id = `goto:${destination.section}/${destination.to}`;
      return [
        {
          id,
          telemetryId: id,
          label: destination.label,
          description: [sectionItem.primary, ...destination.trail].join(" › "),
          group: "Go to",
          Icon: sectionItem.Icon,
          keywords: [
            ...destination.trail,
            ...(destination.keywords ?? []),
            path.replace(/^\//, "")
          ],
          searchOnly: true,
          perform: () => navigate(path)
        }
      ];
    });
  }, [
    enabled,
    navigate,
    flags,
    currentUser,
    currentPractice,
    currentClinic,
    hasRegions,
    roles,
    reportAccess,
    practiceBillingSettings,
    visibleMenuItems,
    useV3
  ]);
}
